// The time-series façade over NwChart, kept because Outlook's disk-trend and
// projection charts speak in series/xTick/nowX rather than in chart blocks.
//
// Its public API is frozen: same props, same rendering. Everything below just
// translates those props into a ChartSpec and hands it to the one primitive —
// the chart grammar (2px lines, no dots at rest, index tooltip, legend only
// for ≥2 series, threshold as a dashed status line, value axis from 0, text in
// ink/faint tokens) now lives in chartOptions, where docs/charts.md documents
// it and chartOptions.test.ts pins it.

import { useMemo } from 'react'

import { NwChart } from './NwChart'
import type { ChartSpec } from './chartSpec'
import { DEFAULT_TOKENS } from './chartTheme'

/** One plotted series. `points` use numeric x (epoch seconds or an index — the
 *  axis is linear either way; `xTick` turns raw x into a human label). */
export interface NwSeries {
  label: string
  points: { x: number; y: number }[]
  /** CSS var holding the stroke color in this chart's cascade. */
  colorVar: string
  /** Used when the var is unset (e.g. bare host page without tokens). */
  fallback: string
  /** Dashed stroke — the projection/prediction treatment. */
  dashed?: boolean
  /** 0..1 area fill under the line; omit for none. */
  fillAlpha?: number
}

export interface NwLineChartProps {
  series: NwSeries[]
  ariaLabel: string
  unit?: string
  /** Pin the y max (percent charts pass 100 so slopes read truthfully);
   *  omit to auto-scale a little above the peak. y always starts at 0. */
  yMax?: number
  /** Dashed horizontal status line + right-aligned label (e.g. 90%). */
  threshold?: { y: number; label: string } | null
  /** Vertical dashed divider (the measured→predicted "now" boundary). */
  nowX?: number | null
  /** Format a raw x value into a tick/tooltip label. */
  xTick: (x: number) => string
  /** Explicit x positions to label (default: first + last of all data). */
  xTickValues?: number[]
  /** Direct-label the last point of the first series with its value. */
  lastValueLabel?: boolean
  height?: number
  /** Token vars for chart chrome, resolved against the same container. */
  inkVar?: string
  faintVar?: string
  gridVar?: string
  thresholdVar?: string
  surfaceVar?: string
}

const DEFAULTS = DEFAULT_TOKENS

export function NwLineChart({
  series,
  ariaLabel,
  unit = '',
  yMax,
  threshold = null,
  nowX = null,
  xTick,
  xTickValues,
  lastValueLabel = false,
  height = 150,
  inkVar = DEFAULTS.ink[0],
  faintVar = DEFAULTS.faint[0],
  gridVar = DEFAULTS.grid[0],
  thresholdVar = DEFAULTS.threshold[0],
  surfaceVar = DEFAULTS.surface[0],
}: NwLineChartProps) {
  const spec: ChartSpec = useMemo(() => {
    const allPoints = series.flatMap((s) => s.points)
    const xs = allPoints.map((p) => p.x)
    const dataMax = Math.max(...allPoints.map((p) => p.y), threshold?.y ?? -Infinity, 1)
    return {
      type: 'line',
      title: '',
      unit,
      threshold: threshold ? threshold.y : null,
      categorical: false,
      labels: [],
      series: series.map((s) => ({
        label: s.label,
        points: s.points,
        data: [],
        unit,
        axis: 'y' as const,
        colorVar: s.colorVar,
        fallback: s.fallback,
        dashed: Boolean(s.dashed),
        fillAlpha: s.fillAlpha ?? 0,
        muted: false,
      })),
      units: [unit],
      stacked: false,
      horizontal: false,
      xLabel: '',
      yLabel: '',
      yMax: yMax ?? null,
      suggestedMax: yMax == null ? Math.ceil(dataMax * 1.1) : null,
      suggestedMaxRight: null,
      timed: false,
      xSpan: xs.length ? Math.max(...xs) - Math.min(...xs) : 0,
      xTickFormat: xTick,
      xTickValues: xTickValues ?? (xs.length ? [Math.min(...xs), Math.max(...xs)] : []),
      lastValueLabel,
      legacy: false,
      height,
      notes: [],
    }
  }, [series, unit, yMax, threshold, xTick, xTickValues, lastValueLabel, height])

  return (
    <NwChart
      spec={spec}
      ariaLabel={ariaLabel}
      height={height}
      // The caller's threshold label wins: Outlook words its own ("90%"),
      // and nowX is a time-series concept a chart block never carries.
      decor={{ threshold, nowX }}
      inkVar={inkVar}
      faintVar={faintVar}
      gridVar={gridVar}
      thresholdVar={thresholdVar}
      surfaceVar={surfaceVar}
    />
  )
}
